import { createQueryFn } from '@oneview/redux/utils';
import { Group, OverExposedType, TopPositionsType } from '@orbis-systems/orbis-api-middleware';
import { showToast } from '@/redux';
import { uniq } from 'lodash';
import { api } from './baseApi';
import { DataTags } from './dataTags';

export const groupApi = api.injectEndpoints({
    endpoints: (build) => ({
        getGroups: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.group.getGroups()),
            providesTags: [DataTags.GetGroups],
        }),
        getGroup: build.query({
            queryFn: createQueryFn((groupId: number, { extra: api }) => api.orbisMiddleware.group.getGroup(groupId)),
            providesTags: [DataTags.GetGroup],
        }),
        getGroupPortfolio: build.query({
            queryFn: createQueryFn(
                (
                    {
                        groupId,
                        combined = true,
                        currency,
                        symbol,
                    }: { groupId: number; currency?: string; combined?: boolean; symbol?: string },
                    { extra: api },
                ) => api.orbisMiddleware.group.getPortfolio(groupId, { combined, currency, symbol }),
            ),
        }),
        getGroupAccountTypesBreakdown: build.query({
            queryFn: createQueryFn((groupId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getGroupAccountTypesBreakdownStat(groupId),
            ),
        }),
        getGroupMarketCapBreakdown: build.query({
            queryFn: createQueryFn((groupId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getGroupMarketCapBreakdownStat(groupId),
            ),
        }),
        getGroupSectorsBreakdown: build.query({
            queryFn: createQueryFn((groupId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getGroupSectorsBreakdownStat(groupId),
            ),
        }),
        getGroupAccountSizesBreakdown: build.query({
            queryFn: createQueryFn((groupId: number, { extra: api }) =>
                api.orbisMiddleware.stat.getGroupAccountSizesBreakdownStat(groupId),
            ),
        }),
        getGroupTopPositionsBreakdown: build.query({
            queryFn: createQueryFn(
                (
                    { groupId, by = TopPositionsType.Weight }: { groupId: number; by?: TopPositionsType },
                    { extra: api },
                ) => api.orbisMiddleware.stat.getGroupTopPositionsStat(groupId, by),
            ),
        }),
        getGroupOverExposedAccounts: build.query({
            queryFn: createQueryFn(
                (
                    { groupId, threshold = 0, type }: { groupId: number; threshold: number; type: OverExposedType },
                    { extra: api },
                ) => api.orbisMiddleware.group.getOverExposedAccounts(groupId, threshold, type),
            ),
        }),
        createGroup: build.mutation({
            queryFn: createQueryFn(
                async (group: Pick<Group, 'title' | 'type' | 'description'>, { extra: api, dispatch }) => {
                    const response = await api.orbisSdk.post('v1/groups/manage', group, { urlQuery: 'create' });
                    dispatch(showToast('Group created'));
                    return response.data;
                },
                {
                    pendingMessage: 'Creating group…',
                },
            ),
            invalidatesTags: [DataTags.GetGroups, DataTags.GetAccounts],
        }),
        updateGroup: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        group,
                        currentAccountIds,
                    }: { group: Pick<Partial<Group>, 'id'> & Partial<Group>; currentAccountIds: number[] },
                    { extra: api, dispatch },
                ) => {
                    const response = await api.orbisSdk.post(
                        'v1/groups/manage',
                        {
                            ...group,
                            accountIds: uniq(group.accountIds || currentAccountIds),
                        },
                        { urlQuery: 'update' },
                    );

                    dispatch(showToast('Group updated'));
                    return response.data;
                },
                {
                    pendingMessage: 'Updating group…',
                },
            ),
            invalidatesTags: [DataTags.GetGroups, DataTags.GetAccounts, DataTags.GetGroup],
        }),
        deleteGroup: build.mutation({
            queryFn: createQueryFn(
                async (groupId: number, { extra: api, dispatch }) => {
                    const response = await api.orbisSdk.post(
                        'v1/groups/manage',
                        { id: groupId },
                        { urlQuery: 'delete' },
                    );
                    dispatch(showToast('Group deleted'));
                    return response.data;
                },
                {
                    pendingMessage: 'Deleting group…',
                },
            ),
            invalidatesTags: [DataTags.GetGroups, DataTags.GetAccounts],
        }),
    }),
});

export const {
    useGetGroupsQuery,
    useGetGroupQuery,
    useLazyGetGroupQuery,
    useLazyGetGroupPortfolioQuery,
    useGetGroupAccountTypesBreakdownQuery,
    useGetGroupMarketCapBreakdownQuery,
    useGetGroupSectorsBreakdownQuery,
    useGetGroupAccountSizesBreakdownQuery,
    useGetGroupTopPositionsBreakdownQuery,
    useGetGroupOverExposedAccountsQuery,
    useCreateGroupMutation,
    useUpdateGroupMutation,
    useDeleteGroupMutation,
} = groupApi;
